<template>
	<div id="full-table-wrapper">
		<div id="returns-header-wrapper">
			<table>
				<thead>
					<tr>
						<th rowspan="2" scope="col">Fund Name</th>
						<th colspan="2" scope="col">
							<span class="large-text">
								YTD Returns at NAV (%)
								<slot name="returnsYTD" />
							</span>
						</th>
						<th colspan="6" scope="col">
							<span class="large-text">Average Annual Total Returns at NAV (%)</span>
							<br />
							<span>As of {{ totalReturnsDate }}</span>
						</th>

						<th colspan="2" scope="col">
							<span class="large-text">
								Expense Ratio (%)
								<slot name="returnsExpenseRatio" />
							</span>
						</th>
						<th scope="col" />
					</tr>
					<tr>
						<th scope="col">
							Daily
							<br />
							As of {{ DailyYTDReturnAsOfDate }}
						</th>
						<th scope="col">
							Month-End
							<br />
							As of {{ YTDReturnAsOfDate }}
						</th>
						<th scope="col">
							1 yr.
							<slot name="returnsYTD1yr" />
						</th>
						<th scope="col">
							3 yr.
							<slot name="returnsYTD3yr" />
						</th>
						<th scope="col">
							5 yr.
							<slot name="returnsYTD5yr" />
						</th>
						<th scope="col">
							10 yr.
							<slot name="returnsYTD10yr" />
						</th>
						<th scope="col">
							15 yr.
							<slot name="returnsYTD15yr" />
						</th>
						<th scope="col">Fund Lifetime</th>
						<th scope="col">Gross</th>
						<th scope="col">Net</th>
						<th scope="col" />
					</tr>
				</thead>
			</table>
		</div>
		<div id="returns-table-wrapper">
			<table>
				<!-- this exists as a space filler responsibile for the header wrapper table, allowing for the main table to not slip under the header -->
				<thead>
					<tr>
						<th rowspan="2" scope="row">Fund Name</th>
						<th colspan="2" scope="colgroup">
							<span class="large-text">YTD Returns at NAV (%)</span>
						</th>
						<th colspan="6" scope="colgroup">
							<span class="large-text">Average Annual Total Returns at NAV (%)</span>
							<br />
							<span>As of {{ totalReturnsDate }} (updated {{ timeSpan }})</span>
						</th>

						<th colspan="2" scope="colgroup">
							<span class="large-text">
								Expense Ratio (%)
								<slot name="returnsExpenseRatio" />
							</span>
						</th>
						<th scope="col" />
					</tr>
					<tr>
						<th scope="col">
							Daily
							<br />
							As of {{ DailyYTDReturnAsOfDate }}
						</th>
						<th scope="col">
							Month-End
							<br />
							As of {{ YTDReturnAsOfDate }}
						</th>
						<th scope="col">1 yr.</th>
						<th scope="col">3 yr.</th>
						<th scope="col">5 yr.</th>
						<th scope="col">10 yr.</th>
						<th scope="col">15 yr.</th>
						<th scope="col">Fund Lifetime</th>
						<th scope="col">Gross</th>
						<th scope="col">Net</th>
						<th scope="col" />
					</tr>
				</thead>
				<tbody v-if="widgetData">
					<template v-for="(category, index) in tableData" :key="category.key">
						<tr
							:class="widgetData.audience === 'institutional' ? 'inst-objective-row' : `category-${category.key.replace(/\s/g, '-').toLowerCase()}`"
							class="category"
						>
							<td>{{ category.key }}</td>
							<td colspan="11" />
						</tr>
						<template
							v-for="(data, index_nested) in category.value"
							:key="`category-${index}-data-${index_nested}`"
						>
							<tr :class="getRowColorClassName(index_nested)" class="fund-row">
								<td>
									<a :href="dynamicDetailPageLink(data.URLName)">{{  data.fundNameLegal }}</a>
								</td>
								<td>{{ data.dailyReturnsYTD }}</td>
								<td>{{ data.returnsYTD }}</td>
								<td>{{ data.returns1Yr }}</td>
								<td>{{ data.returns3Yr }}</td>
								<td>{{ data.returns5Yr }}</td>
								<td>{{ data.returns10Yr }}</td>
								<td>{{ data.returns15Yr }}</td>
								<td>
									{{ data.returnsLTD }}
									<div class="lifetime-date">{{ data.fundInceptionDate }}</div>
								</td>
								<td>{{ data.expenseRatioGross }}</td>
								<td>{{ data.expenseRatioNet }}</td>
								<td />
							</tr>
							<tr v-if="showIndicies" :class="getRowColorClassName(index_nested)" class="index-row">
								<td>
									<sticky-text-wrap
										:textContent="indexTextContent({ indexName: data.indexName, superscriptSlotName: `disclosure_index${data.indexId}`, iBallSlotName: `i_ball_index${data.indexId}`})"
									>
										<template v-slot:[`disclosure_index${data.indexId}`]>
											<slot :name="`disclosure_index${data.indexId}`" />
										</template>
										<template v-slot:[`i_ball_index${data.indexId}`]>
											<slot :name="`i_ball_index${data.indexId}`" />
										</template>
									</sticky-text-wrap>
								</td>
								<td>N/A</td>
								<td>{{ data.indexReturnsYTD }}</td>
								<td>{{ data.indexReturns1Yr }}</td>
								<td>{{ data.indexReturns3Yr }}</td>
								<td>{{ data.indexReturns5Yr }}</td>
								<td>{{ data.indexReturns10Yr }}</td>
								<td>{{ data.indexReturns15Yr }}</td>
								<td>{{ data.indexReturnsLifetime }}</td>
								<td>N/A</td>
								<td>N/A</td>
								<td />
							</tr>
						</template>
					</template>
				</tbody>
				<tfoot>
					<tr>
						<td colspan="12">{{ countText }}</td>
					</tr>
				</tfoot>
			</table>
		</div>
	</div>
</template>

<script>
export default {
	name: 'ReturnsTable',
	props: ['widgetData'],
	computed: {
		componentData() {
			return this.widgetData || {};
		},
		showIndicies() {
			return !!this.componentData.showIndices;
		},
		YTDReturnAsOfDate() {
			return (
				(this.componentData.dates &&
					this.componentData.dates.YTDReturnAsOfDate) ||
				''
			);
		},
		DailyYTDReturnAsOfDate() {
			return (
				(this.componentData.dates &&
					this.componentData.dates.DailyYTDReturnAsOfDate) ||
				''
			);
		},
		totalReturnsDate() {
			if (this.timeSpan === 'monthly') {
				return (
					(this.componentData.dates &&
						this.componentData.dates.ReturnsDateMonthly) ||
					'--'
				);
			}
			return (
				(this.componentData.dates &&
					this.componentData.dates.ReturnsDateQuarterly) ||
				'--'
			);
		},
		selectedObjectives() {
			return this.componentData.selectedObjectives || [];
		},
		selectedAssetTypes() {
			return this.componentData.selectedAssetTypes || [];
		},
		tableData() {
			return this.componentData.tableData || [];
		},
		timeSpan() {
			return this.componentData.timeSpan || 'monthly';
		},
		countText() {
			if (this.tableData.length) {
				const length = this.tableData.reduce((acc, cur) => {
					return [...acc, ...cur.value];
				}, []).length;
				return `1-${length} of ${length} results`;
			}
			return '0 results';
		}
	},
	mounted() {
		this.setHorizontalScrollOnTable();
	},
	methods: {
		setHorizontalScrollOnTable() {
			const slider = document.querySelector('#returns-table-wrapper');
			const headerSlider = document.querySelector(
				'#returns-header-wrapper'
			);
			let isDown = false;
			let startX;
			let scrollLeft;
			slider.addEventListener('mousedown', e => {
				e.preventDefault();
				isDown = true;
				startX = e.pageX - slider.offsetLeft;
				scrollLeft = slider.scrollLeft;
			});
			headerSlider.addEventListener('mousedown', e => {
				e.preventDefault();
				isDown = true;
				startX = e.pageX - slider.offsetLeft;
				scrollLeft = slider.scrollLeft;
			});
			document.addEventListener('mouseup', () => {
				isDown = false;
			});
			document.addEventListener('mousemove', e => {
				if (!isDown) return;
				e.preventDefault();
				const x = e.pageX - slider.offsetLeft;
				const walk = x - startX;
				slider.scrollLeft = scrollLeft - walk;
				headerSlider.scrollLeft = scrollLeft - walk;
			});
		},
		getRowColorClassName(index) {
			return index % 2 === 0 ? 'white' : 'gray';
		},
		indexTextContent({ indexName, superscriptSlotName, iBallSlotName }) {
			return [
				{ content: [indexName.slice(0, indexName.lastIndexOf(' '))] },
				{
					noWrap: true,
					content: [
						`${indexName.split(' ').pop()} `,
						{ slotName: superscriptSlotName },
						' ',
						{ slotName: iBallSlotName }
					]
				}
			];
		}
	}
};
</script>

<style lang="less" scoped>
@import '~_css_/variables';
//mixins
.column-border-left() {
	border-left: 1px solid rgb(212, 212, 212);
}
.column-border-right() {
	border-right: 1px solid rgb(212, 212, 212);
}
.left-cell-scroll-border() {
	position: sticky;
	left: 0;
	border-right: 8px solid #d4d4d4;
	border-image-width: 0 8px 0px 0;
	border-image-outset: 0 8px 0px 0;
	border-image-slice: 25%;
	background-origin: inherit repeat;
	border-image-source: linear-gradient(
		90deg,
		transparent 75%,
		rgba(0, 0, 0, 0.3) 0,
		transparent
	);
}
.right-cell-scroll-border() {
	position: sticky;
	right: 0;
	padding: 0;
	border-width: 0 0 0 8px;
	border-style: solid;
	border-image-width: 0 0 0 8px;
	border-image-source: linear-gradient(
		90deg,
		transparent 0%,
		rgba(0, 0, 0, 0.3) 28%,
		transparent
	);
	border-image-outset: 0 0 0 0px;
	border-image-slice: 30%;
	background-origin: inherit repeat;
	width: 1px;
}
//vars
@slightly-gray: #f2f2f2;
@table-header-text-color: #666;
@table-darker-text-color: #333;
@table-light-text-color: #888;
@button-text-color: #009adf;
#full-table-wrapper {
	position: relative;
}
#returns-header-wrapper {
	width: 100%;
	position: sticky;
	top: 0;
	z-index: 1;
	margin: auto;
	overflow: scroll;
	overflow-x: hidden;
	overflow-y: hidden;
}
#returns-header-wrapper,
#returns-table-wrapper {
	table {
		width: 970px;
		table-layout: fixed;
		border-collapse: collapse;
		thead {
			tr {
				font-family: @avenir-condensed;
				font-size: 12px;
				color: @table-header-text-color;
				th {
					vertical-align: bottom;
					text-align: center;
					padding: 10px;
					background-color: @slightly-gray;
					.large-text {
						font-family: @avenir-demi-condensed-sans;
					}
				}
				th:nth-child(8) {
					text-align: center;
					width: 100px;
				}
				th:last-of-type {
					width: 0;
				}
				&:nth-of-type(2) {
					th:nth-of-type(8) {
						.column-border-right();
					}
					th:nth-of-type(1) {
						.column-border-left();
					}
					th:nth-of-type(2) {
						.column-border-right();
					}
				}
				&:nth-of-type(1) {
					th:nth-child(1) {
						text-align: left;
						width: 300px;
						z-index: 1;
					}
					th:nth-of-type(2) {
						.column-border-left();
						.column-border-right();
						vertical-align: top;
						padding-top: 5px;
						width: 200px;
					}
					th:nth-of-type(3) {
						vertical-align: top;
						padding-top: 5px;
					}
					th:nth-of-type(4) {
						vertical-align: top;
						padding-top: 5px;
						.column-border-left();
						width: 81px;
					}
				}
			}
		}
	}
}
#returns-table-wrapper {
	width: 100%;
	position: relative;
	margin: auto;
	overflow-x: scroll;
	table {
		width: 970px;
		table-layout: fixed;
		border-collapse: collapse;
		thead {
			visibility: collapse;
		}
		tbody {
			.category td {
				background-color: #7c8a8d;
				font-size: 14px;
				padding: 10px;
				height: 30px;
				color: white;
				font-family: @avenir-condensed;
			}
			@colors: #002d72, #0057b8, #6f928a, #2dccd3, #008eaa, #00965e,
				#7c878e, #7c878e, #7c878e, #7c878e;
			@objectives: growth, growth-and-income, asset-allocation, balanced,
				equity-income, bond, managed-risk, portfolio-series;
			.category-colors(@objectives; @colors; @index) when (@index < 9) {
				.category-colors(@objectives; @colors; (@index + 1));

				@color: extract(@colors, @index);
				@name: extract(@objectives, @index);

				.category-@{name} td {
					color: white;
					background-color: @color;
				}
			}
			.category-colors(@objectives, @colors, 1);
			.inst-objective-row td {
				color: white;
				font-family: @avenir-condensed;
				background-color: @table-light-text-color;
			}
			tr:not(.category) {
				height: 40px; //height works the same as min height if the content is too great to be contained
				&.white,
				&.white td:first-of-type {
					background-color: white;
				}
				&.gray,
				&.gray td:first-of-type {
					background-color: @slightly-gray;
				}
				&.fund-row td {
					color: @table-darker-text-color;
				}
				&.index-row td {
					&:first-of-type {
						font-weight: normal;
					}
					color: @table-light-text-color;
				}
				td {
					&:first-of-type {
						padding-left: 20px;
						text-align: left;
						vertical-align: middle;
						.column-border-right();
					}
					&:nth-child(3),
					&:nth-child(9) {
						.column-border-right();
					}
					text-align: center;
					vertical-align: middle;
					font-family: @avenir-demi-condensed;
					padding: 10px 5px;
					font-weight: 400;
					color: @table-darker-text-color;
					font-size: 12px;
					.lifetime-date {
						font-size: 12px;
						font-family: @avenir-condensed;
						color: @table-light-text-color;
					}
					a {
						text-align: left;
						color: @button-text-color;
						border: none;
						padding: 0;
						background-color: transparent;
					}
					&:last-of-type {
						width: 0px;
					}
				}
			}
		}
		tfoot {
			td {
				background-color: #f2f2f2;
				border-top: 1px solid #d4d4d4;
				border-bottom: 1px solid #d4d4d4;
				height: 30px;
				left: 0;
				padding: 10px;
				position: sticky;
				right: 0;
			}
		}
	}
}

@media (max-width: 996px) {
	#returns-table-wrapper,
	#returns-header-wrapper {
		table {
			border-collapse: initial;
			tbody tr {
				td:first-of-type {
					position: sticky;
					left: 0;
				}
				&:not(.category) {
					td {
						&:first-of-type {
							.left-cell-scroll-border();
						}
						&:last-of-type {
							.right-cell-scroll-border();
						}
					}
				}
			}
			thead {
				tr:first-of-type {
					th:nth-of-type(2) {
						border-left: none;
					}
					th:first-of-type {
						.left-cell-scroll-border();
					}
				}
				tr th:last-of-type {
					.right-cell-scroll-border();
				}
			}
		}
	}
}
@media (max-width: 600px) {
	#returns-table-wrapper,
	#returns-header-wrapper {
		table {
			thead {
				tr:first-of-type {
					th:first-of-type {
						width: 50vw;
					}
				}
			}
		}
	}
}
</style>
